/**
 * ScorePanel — the Diversification Score at the top of the Portafoglio level.
 *
 * The score is a weighted average of the family subscores and nothing else: the
 * panel therefore shows the composite, how supported it is by the data, every
 * family with the dimension actually used, and a deterministic explanation of
 * what holds it up and what pulls it down. When the data do not support a
 * composite, the number disappears and the family readings stay.
 *
 * Each family is read on the *guaranteed* number of equivalent exposures
 * (`effective_number_floor`, worst case for the unclassified mass), so missing
 * data can never improve the score, and the calibration that produced the
 * number is published next to it.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2 } from "lucide-react";
import { SectionCard } from "@/components/system/SectionCard";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELIABILITY = {
  high: { label: "Affidabilità alta", variant: "success" },
  usable: { label: "Affidabilità media", variant: "warning" },
  limited: { label: "Affidabilità limitata", variant: "warning" },
  insufficient: { label: "Dati insufficienti", variant: "danger" },
};

const FAMILY_STATUS = {
  supported: { label: "Inclusa", variant: "secondary" },
  not_applicable: { label: "N/A", variant: "secondary" },
  not_supported: { label: "Dati insufficienti", variant: "danger" },
};

const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${Number(value).toFixed(digits)}%`;

export function ScorePanel({ sessionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/diversification/score/${sessionId}`)
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError("Non è stato possibile calcolare il Diversification Score.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-content-muted" data-testid="score-loading">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        Calcolo del Diversification Score…
      </div>
    );
  }
  if (error) return <p className="text-sm text-danger" data-testid="score-error">{error}</p>;
  if (!data) return null;

  const quality = data.quality || {};
  const reliability = RELIABILITY[quality.reliability] || RELIABILITY.insufficient;
  const computable = data.status === "ok";

  return (
    <SectionCard
      testId="score-panel"
      title="Diversification Score"
      description="Media pesata dei subscore delle famiglie, letti sul numero di esposizioni equivalenti che i dati garantiscono: la massa non classificata è contata nel modo peggiore possibile, così un dato mancante non può alzare il punteggio. Sovrapposizione e duplicazione restano strumenti diagnostici e non entrano nel punteggio."
      density="compact"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <p
              className="text-4xl font-semibold tabular text-content leading-none"
              data-testid="score-value"
            >
              {computable ? Number(data.score).toFixed(0) : "n/d"}
              <span className="text-base font-normal text-content-muted"> / 100</span>
            </p>
            <p className="text-label text-content-muted mt-1" data-testid="score-reason">
              {data.reason}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={reliability.variant} data-testid="score-reliability">
              {reliability.label}
            </Badge>
            <Badge variant="secondary" data-testid="score-support">
              {quality.families_supported}/{quality.families_total} famiglie ·{" "}
              {pct(quality.support_ratio_pct, 0)} dei pesi
            </Badge>
            {(quality.flags || []).map((flag) => (
              <Badge key={flag} variant="secondary" data-testid={`score-flag-${flag}`}>
                {flag === "coverage_limited"
                  ? "coverage limitata"
                  : flag === "family_not_applicable"
                    ? "famiglia N/A"
                    : flag === "calibration_overridden"
                      ? "calibrazione non standard"
                      : flag}
              </Badge>
            ))}
            {data.calibration?.calibration_id && (
              <Badge variant="secondary" data-testid="score-calibration">
                Calibrazione {data.calibration.calibration_id}
              </Badge>
            )}
          </div>
        </div>

        {(data.warnings || []).map((warning) => (
          <p
            key={warning}
            role="status"
            data-testid="score-warning"
            className="flex items-start gap-2 text-label text-warning"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            {warning}
          </p>
        ))}

        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full text-label" data-testid="score-families">
            <thead>
              <tr className="bg-surface-subtle text-content-muted">
                <th scope="col" className="px-3 py-2 text-left font-medium">Famiglia</th>
                <th scope="col" className="px-3 py-2 text-left font-medium">Dimensione usata</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">Subscore</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">Esposizioni equiv. garantite</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">Coverage</th>
                <th scope="col" className="px-3 py-2 text-right font-medium">Peso nel punteggio</th>
              </tr>
            </thead>
            <tbody>
              {data.families.map((family) => {
                const status = FAMILY_STATUS[family.status] || FAMILY_STATUS.not_supported;
                return (
                  <tr
                    key={family.family}
                    className="border-t border-line align-top"
                    data-testid={`score-family-${family.family}`}
                  >
                    <td className="px-3 py-2">
                      <span className="block text-content">{family.label}</span>
                      {family.status !== "supported" && (
                        <Badge
                          variant={status.variant}
                          className="mt-1"
                          data-testid={`score-family-${family.family}-status`}
                        >
                          {status.label}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-content-secondary">
                      {family.dimension_label || "—"}
                      <span
                        className="block text-content-muted"
                        data-testid={`score-family-${family.family}-reason`}
                      >
                        {family.reason}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular font-medium text-content">
                      {family.subscore == null ? "n/d" : Number(family.subscore).toFixed(0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-content-secondary">
                      {(() => {
                        const guaranteed =
                          family.effective_number_floor ?? family.effective_number;
                        if (guaranteed == null) return "n/d";
                        const observed = family.effective_number;
                        const differs =
                          observed != null &&
                          Math.abs(observed - guaranteed) >= 0.05;
                        return (
                          <>
                            {`${Number(guaranteed).toFixed(1)} / ${family.en_target}`}
                            {differs && (
                              <span
                                className="block text-content-muted"
                                data-testid={`score-family-${family.family}-observed`}
                              >
                                osservate {Number(observed).toFixed(1)}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-content-muted">
                      {pct(family.coverage_pct, 1)}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-content-muted">
                      {family.in_composite ?? family.status === "supported"
                        ? pct(family.effective_weight_pct, 1)
                        : "esclusa"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Statements
            testId="score-strengths"
            title="Cosa sostiene il punteggio"
            items={data.explanation?.strengths}
            empty="Nessuna famiglia misurabile su questi dati."
          />
          <Statements
            testId="score-concentrations"
            title="Cosa lo abbassa"
            items={data.explanation?.concentrations}
            empty="Nessuna concentrazione rilevante da segnalare."
          />
        </div>
      </div>
    </SectionCard>
  );
}

function Statements({ title, items, empty, testId }) {
  return (
    <div className="rounded-lg border border-line overflow-hidden">
      <h3 className="bg-surface-subtle px-3 py-2 text-label font-medium text-content-secondary">
        {title}
      </h3>
      {items && items.length > 0 ? (
        <ul data-testid={testId}>
          {items.map((item) => (
            <li
              key={`${item.family}-${item.text}`}
              className="px-3 py-1.5 text-label text-content border-t border-line first:border-t-0"
              data-testid={`${testId}-${item.family}`}
            >
              {item.text}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-2 text-label text-content-muted" data-testid={`${testId}-empty`}>
          {empty}
        </p>
      )}
    </div>
  );
}

export default ScorePanel;
